import { useState, useCallback } from 'react';
import { Skill, SkillEffect } from '../types';

interface ActiveEffect {
  id: string;
  name: string;
  description: string;
  duration: number;
  remainingTime: number;
  value?: number;
  stacks?: number;
  icon?: string;
  startTime: number; // 添加开始时间
  effectType?: 'buff' | 'debuff' | 'proc'; // 效果类型
}

interface EffectState {
  [effectId: string]: ActiveEffect;
}

export const useSkillEffects = () => {
  const [activeEffects, setActiveEffects] = useState<EffectState>({});

  // 处理技能效果
  const processSkillEffects = useCallback((skill: Skill) => {
    if (!skill.effects || skill.effects.length === 0) return;

    skill.effects.forEach(effect => {
      switch (effect.type) {
        case 'proc':
          handleProcEffect(skill, effect);
          break;
        case 'buff':
          handleBuffEffect(skill, effect);
          break;
        case 'debuff':
          handleDebuffEffect(skill, effect);
          break;
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 处理概率效果（动态基于配置）
  const handleProcEffect = (skill: Skill, effect: SkillEffect) => {
    // 检查条件是否满足
    if (effect.conditions && effect.conditions.length > 0) {
      const conditionsMet = effect.conditions.every(condition => {
        switch (condition.type) {
          case 'status':
            const statusId = condition.statusId || condition.condition;
            return activeEffects[statusId] && activeEffects[statusId].remainingTime > 0;
          default:
            return false;
        }
      });
      
      if (!conditionsMet) {
        // 条件不满足，不触发效果
        return;
      }
    }
    
    const procChance = effect.value || 0;
    const randomValue = Math.random() * 100;
    
    if (randomValue <= procChance) {
      // 从effect配置中获取要触发的状态效果信息
      const effectData = parseProcEffectData(effect);
      if (effectData) {
        addEffect({
          id: effectData.id,
          name: effectData.name,
          description: effectData.description,
          duration: effectData.duration,
          remainingTime: effectData.duration,
          value: effectData.value,
          stacks: effectData.stacks,
          icon: effectData.icon,
          startTime: Date.now(),
          effectType: 'proc'
        });
        
        console.log(`${skill.name} 触发了${effectData.name}！`);
      }
    } else {
      console.log(`${skill.name} 没有触发概率效果（${procChance}%）`);
    }
  };

  // 解析概率效果数据（严格基于配置）
  const parseProcEffectData = (effect: SkillEffect) => {
    // 必须提供完整的效果配置
    if (!effect.effectId || !effect.effectName) {
      throw new Error(`Proc effect missing required configuration: effectId and effectName are required. Got: ${JSON.stringify(effect)}`);
    }
    
    return {
      id: effect.effectId,
      name: effect.effectName,
      description: effect.effectDescription || effect.description,
      duration: effect.duration || 30,
      value: effect.value,
      stacks: effect.stacks,
      icon: effect.effectIcon
    };
  };

  // 处理增益效果（严格基于配置）
  const handleBuffEffect = (skill: Skill, effect: SkillEffect) => {
    if (!effect.effectId || !effect.effectName) {
      console.warn(`Buff effect missing configuration for skill ${skill.name}, using fallback values. Effect:`, effect);
      return; // 暂时跳过缺少配置的效果
    }
    
    const effectId = effect.effectId;
    const effectName = effect.effectName;
    const duration = effect.duration || 30;
    
    addEffect({
      id: effectId,
      name: effectName,
      description: effect.effectDescription || effect.description,
      duration: duration,
      remainingTime: duration,
      value: effect.value,
      stacks: effect.stacks,
      icon: effect.effectIcon,
      startTime: Date.now(),
      effectType: 'buff'
    });
    
    console.log(`${skill.name} 施加了增益效果：${effectName}`);
  };

  // 处理减益效果（严格基于配置）
  const handleDebuffEffect = (skill: Skill, effect: SkillEffect) => {
    if (!effect.effectId || !effect.effectName) {
      console.warn(`Debuff effect missing configuration for skill ${skill.name}, using fallback values. Effect:`, effect);
      return; // 暂时跳过缺少配置的效果
    }
    
    const effectId = effect.effectId;
    const effectName = effect.effectName;
    const duration = effect.duration || 30;
    
    addEffect({
      id: effectId,
      name: effectName,
      description: effect.effectDescription || effect.description,
      duration: duration,
      remainingTime: duration,
      value: effect.value,
      stacks: effect.stacks,
      icon: effect.effectIcon,
      startTime: Date.now(),
      effectType: 'debuff'
    });
    
    console.log(`${skill.name} 施加了减益效果：${effectName}`);
  };


  // 添加效果（支持层数叠加）
  const addEffect = (effect: ActiveEffect) => {
    setActiveEffects(prev => {
      const existing = prev[effect.id];
      
      if (existing && effect.stacks) {
        // 如果效果已存在且新效果有层数，进行叠加
        const maxStacks = getMaxStacks(effect.id);
        const newStacks = Math.min((existing.stacks || 1) + (effect.stacks || 1), maxStacks);
        
        return {
          ...prev,
          [effect.id]: {
            ...existing,
            stacks: newStacks,
            // 重新开始计时
            remainingTime: effect.duration,
            startTime: Date.now()
          }
        };
      }
      
      // 新效果或不支持叠加的效果
      return {
        ...prev,
        [effect.id]: effect
      };
    });
  };

  // 获取效果的最大层数（根据效果ID配置）
  const getMaxStacks = (effectId: string): number => {
    switch (effectId) {
      case 'repertoire': // 诗心最多4层
        return 4;
      default:
        return 1;
    }
  };

  // 移除效果
  const removeEffect = (effectId: string) => {
    setActiveEffects(prev => {
      const newEffects = { ...prev };
      delete newEffects[effectId];
      return newEffects;
    });
  };

  // 消耗效果层数
  const consumeEffectStacks = (effectId: string, consumeAmount: number = 1) => {
    setActiveEffects(prev => {
      const effect = prev[effectId];
      if (!effect) return prev;
      
      const currentStacks = effect.stacks || 1;
      const newStacks = Math.max(0, currentStacks - consumeAmount);
      
      if (newStacks <= 0) {
        // 层数归零，移除效果
        const newEffects = { ...prev };
        delete newEffects[effectId];
        console.log(`效果 ${effect.name} 已消耗完毕`);
        return newEffects;
      } else {
        // 减少层数
        console.log(`效果 ${effect.name} 消耗 ${consumeAmount} 层，剩余 ${newStacks} 层`);
        return {
          ...prev,
          [effectId]: {
            ...effect,
            stacks: newStacks
          }
        };
      }
    });
  };

  // 检查是否有特定效果
  const hasEffect = (effectId: string): boolean => {
    return !!activeEffects[effectId] && activeEffects[effectId].remainingTime > 0;
  };

  // 获取效果剩余时间
  const getEffectRemainingTime = (effectId: string): number => {
    return activeEffects[effectId]?.remainingTime || 0;
  };

  // 更新效果时间（基于真实时间计算）
  const updateEffectTimers = useCallback(() => {
    const now = Date.now();
    
    setActiveEffects(prev => {
      const updated = { ...prev };
      let hasChanges = false;

      Object.keys(updated).forEach(effectId => {
        const effect = updated[effectId];
        if (effect.remainingTime > 0) {
          // 基于开始时间和当前时间计算剩余时间
          const elapsed = (now - effect.startTime) / 1000; // 转换为秒
          const newRemainingTime = Math.max(0, effect.duration - elapsed);
          
          if (newRemainingTime !== effect.remainingTime) {
            updated[effectId] = {
              ...effect,
              remainingTime: newRemainingTime
            };
            hasChanges = true;
          }
          
          if (newRemainingTime <= 0) {
            console.log(`效果 ${effect.name} 已消失`);
            delete updated[effectId];
            hasChanges = true;
          }
        }
      });

      return hasChanges ? updated : prev;
    });
  }, []);

  // 清除所有效果
  const clearAllEffects = () => {
    setActiveEffects({});
  };

  return {
    activeEffects,
    processSkillEffects,
    hasEffect,
    getEffectRemainingTime,
    updateEffectTimers,
    removeEffect,
    consumeEffectStacks,
    clearAllEffects
  };
};